import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";

const CHECKS = [
  { key: "stripe_key",            label: "Stripe secret key",              group: "Payments",  critical: true  },
  { key: "stripe_webhook_secret", label: "Stripe webhook secret",          group: "Payments",  critical: true  },
  { key: "membership_price_id",   label: "Stripe membership price ID",     group: "Payments",  critical: true  },
  { key: "coupon_id",             label: "Stripe £150 first-year coupon",  group: "Payments",  critical: true  },
  { key: "generic_payment_link",  label: "Stripe generic payment link",    group: "Payments",  critical: false },
  { key: "membership_payment_link", label: "Stripe membership payment link", group: "Payments", critical: true },
  { key: "resend_key",            label: "Resend API key",                 group: "Email",     critical: true  },
  { key: "admin_email",           label: "Admin notification email",       group: "Email",     critical: false },
  { key: "sms_configured",        label: "Twilio SMS (follow-up channel)", group: "Automation", critical: false },
  { key: "whatsapp_configured",   label: "WhatsApp Business (optional)",   group: "Automation", critical: false },
  { key: "object_storage",        label: "Object storage available",       group: "Storage",   critical: true  },
];

export default function Settings() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminApi.get("/settings")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load settings"));
  }, []);

  if (err) return <div className="text-[#DC2626]" data-testid="settings-error">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-[#475569]"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  const groups = ["Payments", "Email", "Automation", "Storage"];
  const allOk = CHECKS.filter(c => c.critical).every((c) => !!data[c.key]);

  return (
    <div data-testid="admin-settings-page">
      <span className="eyebrow">System status</span>
      <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Settings</h1>
      <p className="text-sm text-[#475569] mt-1">
        Reports which environment variables and services are present. Values are never exposed here — only presence.
      </p>

      <div className={`mt-6 p-5 border-l-4 ${allOk ? "border-[#064E2E] bg-[#064E2E]/5" : "border-[#B88A2A] bg-[#B88A2A]/10"}`} data-testid="settings-overall">
        <div className="flex items-start gap-3">
          {allOk ? <CheckCircle2 className="text-[#064E2E] mt-0.5" /> : <AlertTriangle className="text-[#B88A2A] mt-0.5" />}
          <div>
            <p className="font-display text-xl text-[#071A33]">
              {allOk ? "All required services configured — launch-ready" : "Configuration incomplete — NOT launch-ready"}
            </p>
            <p className="text-sm text-[#475569] mt-1">
              Live Stripe mode: <span className="font-medium">{data.live_mode ? "ON" : "OFF (placeholder keys)"}</span>
            </p>
          </div>
        </div>
      </div>

      {!data.live_mode && (
        <div className="mt-4 p-5 border-l-4 border-[#B88A2A] bg-[#FFF8EC]" data-testid="settings-placeholder-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-[#B88A2A] mt-0.5 shrink-0" />
            <div className="text-sm text-[#3F2E10]">
              <p className="font-display text-base text-[#071A33]">Preview mode — placeholder Stripe keys detected</p>
              <p className="mt-1 text-[#475569]">
                You're still on test/placeholder credentials. <span className="font-medium">No live payments will be processed</span>.
                Before going live, paste your <code className="bg-[#B88A2A]/15 px-1">sk_live_…</code> and <code className="bg-[#B88A2A]/15 px-1">whsec_…</code>
                {" "}values into the Emergent Deploy panel — never into chat or this preview's <code className="bg-[#B88A2A]/15 px-1">.env</code>.
              </p>
              <p className="mt-2 text-xs text-[#475569]">
                After deploy, return here to confirm <span className="font-medium">Live Stripe mode: ON</span>, then run a controlled £99 checkout.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-8">
        {groups.map((g) => (
          <section key={g}>
            <h2 className="font-display text-xl text-[#071A33] mb-3">{g}</h2>
            <div className="bg-white border border-[#E2DCD0] divide-y divide-[#E2DCD0]">
              {CHECKS.filter(c => c.group === g).map((c) => {
                const ok = !!data[c.key];
                return (
                  <div key={c.key} className={`flex items-center justify-between px-5 py-4 ${c.critical && !ok ? "bg-[#DC2626]/5 border-l-4 border-[#DC2626]" : ""}`} data-testid={`setting-${c.key}`}>
                    <div>
                      <p className="text-sm font-medium text-[#071A33]">{c.label}</p>
                      <p className="text-[11px] text-[#475569] font-mono mt-0.5">{c.key.toUpperCase()}{c.critical ? " · REQUIRED FOR LAUNCH" : " · optional"}</p>
                    </div>
                    {ok ? (
                      <span className="inline-flex items-center gap-1.5 text-[#064E2E] text-sm font-medium"><CheckCircle2 size={16}/> Present</span>
                    ) : (
                      <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${c.critical ? "text-[#DC2626]" : "text-[#B88A2A]"}`}>
                        <XCircle size={16}/> {c.critical ? "Missing (blocks launch)" : "Missing"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-10 p-5 bg-[#071A33] text-white">
        <p className="text-[#B88A2A] tracking-[0.18em] uppercase text-xs font-medium mb-2">Go-live checklist</p>
        <ol className="text-sm space-y-1.5 text-white/85 list-decimal pl-5">
          <li>Replace placeholder Stripe keys with live values in <code className="bg-white/10 px-1">/app/backend/.env</code></li>
          <li>Create Stripe product (£249/yr), £150 first-year coupon, copy price/coupon/payment-link IDs</li>
          <li>Add webhook endpoint pointing at <code className="bg-white/10 px-1">/api/stripe/webhook</code> and copy the signing secret</li>
          <li>Set <code className="bg-white/10 px-1">RESEND_API_KEY</code> and a verified sending domain</li>
          <li>Rotate <code className="bg-white/10 px-1">ADMIN_KEY</code> to a strong secret</li>
          <li>Run one controlled £99 live payment end-to-end</li>
        </ol>
      </div>
    </div>
  );
}
